import React from 'react';
import clsx from 'clsx';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  PRESET_GENERATION_PROGRESS_LABELS,
  PRESET_GENERATION_PROGRESS_STEPS,
  type PresetGenerationStatus,
  isPresetGenerationInFlight
} from '~/components/workspaces/lib/convert-to-preset-wizard.utils';
import { usePresetGeneration } from '~/api/data/workspaces/use-preset-generation';
import { type PresetGenerationStep, type StartPresetGenerationInput } from '~/api/graphql';

type StageState = 'done' | 'current' | 'pending';

/**
 * An absent step, or one a newer backend added, reads as the first stage while the
 * run is in flight, so the list is never a row of dead circles.
 */
const stageStates = (
  progressStep: PresetGenerationStep | null,
  inFlight: boolean
): Record<string, StageState> => {
  const knownIndex = progressStep ? PRESET_GENERATION_PROGRESS_STEPS.indexOf(progressStep) : -1;
  const currentIndex = knownIndex >= 0 ? knownIndex : inFlight ? 0 : -1;
  const result: Record<string, StageState> = {};
  PRESET_GENERATION_PROGRESS_STEPS.forEach((step, i) => {
    result[step] = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'pending';
  });
  return result;
};

const StageRow = ({ label, state }: { label: string; state: StageState }) => (
  <li className="flex items-center gap-3">
    {state === 'done' ? (
      <CheckCircleIcon className="size-5 text-sky-500 dark:text-sky-400 shrink-0" aria-hidden />
    ) : state === 'current' ? (
      <span className="relative size-5 shrink-0 flex items-center justify-center" aria-hidden>
        <span className="absolute size-2.5 rounded-full bg-sky-500 animate-ping" />
        <span className="relative size-2.5 rounded-full bg-sky-500" />
      </span>
    ) : (
      <span className="size-5 shrink-0 flex items-center justify-center" aria-hidden>
        <span className="size-2.5 rounded-full border border-neutral-300 dark:border-neutral-600" />
      </span>
    )}
    <span
      className={clsx(
        'text-sm',
        state === 'pending'
          ? 'text-neutral-400 dark:text-neutral-500'
          : 'text-neutral-700 dark:text-neutral-300 font-medium'
      )}
    >
      {label}
    </span>
  </li>
);

/**
 * Step 5: the generation modal body. With an `input`, mounting it starts the run,
 * so the wizard must keep it mounted for as long as progress should keep being
 * polled.
 */
export const ConvertToPresetGenerationStep = ({
  input,
  workspaceId,
  onStatusChange,
  onCompleted,
  onBack,
  onClose
}: {
  input: StartPresetGenerationInput | null;
  workspaceId: string;
  onStatusChange: (status: PresetGenerationStatus) => void;
  onCompleted: (presetId: string) => void;
  onBack: () => void;
  onClose: () => void;
}) => {
  const { status, progressStep, presetId, error, retry, canRetry } = usePresetGeneration(input);

  // An effect, not a render-time call: lifting status must not update the parent
  // during this component's render pass.
  React.useEffect(() => {
    onStatusChange(status);
  }, [status, onStatusChange]);

  React.useEffect(() => {
    if (status === 'completed' && presetId) {
      onCompleted(presetId);
    }
  }, [status, presetId, onCompleted]);

  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center text-center gap-4 py-6">
        <div className="rounded-full bg-red-50 dark:bg-red-500/10 p-3">
          <ExclamationTriangleIcon className="size-6 text-red-500 dark:text-red-400" aria-hidden />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-neutral-900 dark:text-white m-0">
            Preset generation didn&apos;t finish
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 m-0 max-w-sm">
            {error ?? 'Something went wrong while generating the preset.'}
          </p>
        </div>
        {/* Back, not just Retry: a rejected input fails identically on every
            retry, so Close would be the only exit and would discard the form. */}
        <div className="flex items-center gap-2">
          <Button type="button" color="white" onClick={onBack}>
            Back
          </Button>
          <Button type="button" color="white" onClick={onClose}>
            Close
          </Button>
          {canRetry && (
            <Button type="button" color="sky" onClick={retry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  const inFlight = isPresetGenerationInFlight(status);
  const states = stageStates(progressStep, inFlight);

  return (
    <div className="flex flex-col gap-5 py-2">
      <div className="flex items-center gap-3">
        <Spinner size="size-6" color="sky" />
        <div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-white m-0">
            Building your preset…
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 m-0">
            This usually takes a minute or two. Generation runs in the background — you can keep
            working.
          </p>
        </div>
      </div>

      <ul className="flex flex-col gap-3 m-0 p-0 list-none">
        {PRESET_GENERATION_PROGRESS_STEPS.map((step) => (
          <StageRow
            key={step}
            label={PRESET_GENERATION_PROGRESS_LABELS[step]}
            state={states[step]}
          />
        ))}
      </ul>

      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={() =>
            window.open(`/app/workspaces/${workspaceId}`, '_blank', 'noopener,noreferrer')
          }
          className="group inline-flex items-center gap-1.5 text-xs text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 transition-colors duration-150"
        >
          <ArrowTopRightOnSquareIcon className="size-3.5" aria-hidden />
          Continue working in this workspace
        </button>
      </div>
    </div>
  );
};
